import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { ApiError, api, type SessionInfo, type ThisDevice } from "./api";
import { Alert, Working } from "./ui";
import { brandLogo } from "./brand";

/**
 * Signing in, in the template's "sign in basic" page: logo above, one box in the middle.
 *
 * The device says which till this is before anyone types, because it holds the token it
 * was given when it was set up. Nobody types a device letter here: that letter goes inside
 * every sale reference, and a letter a person can choose is one two offline iPads can
 * both claim.
 */
export function SignIn({ onSignedIn }: { readonly onSignedIn: (session: SessionInfo) => void }): React.ReactElement {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [device, setDevice] = useState<ThisDevice | null | undefined>();
  const [settingUp, setSettingUp] = useState(false);
  /** Set once the password is right and the phone still has to agree. */
  const [asking, setAsking] = useState<{ challenge: string; username: string } | undefined>();

  useEffect(() => {
    let live = true;
    api
      .thisDevice()
      .then((result) => {
        if (live) setDevice(result.device);
      })
      .catch(() => {
        if (live) setDevice(null);
      });
    return () => {
      live = false;
    };
  }, []);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      const result = await api.signIn(username, password);
      if (result.kind === "code") {
        setAsking({ challenge: result.challenge, username: result.username });
        setBusy(false);
        return;
      }
      onSignedIn(result.session);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Something went wrong");
      setBusy(false);
    }
  }

  if (asking) {
    return (
      <SecondStep
        username={asking.username}
        challenge={asking.challenge}
        onSignedIn={onSignedIn}
        onCancel={() => {
          setAsking(undefined);
          setPassword("");
        }}
      />
    );
  }

  if (settingUp) {
    return (
      <SetUpDevice
        onDone={(claimed) => {
          setDevice(claimed);
          setSettingUp(false);
        }}
        onCancel={() => setSettingUp(false)}
      />
    );
  }

  return (
    <AuthenticationLayout title="Sign in" subtitle="Point of sale, inventory and accounts">
      <form className="grid grid-cols-12 gap-y-4" onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}

        <div className="col-span-12">
          <label htmlFor="signin-username" className="form-label text-default">
            Username
          </label>
          <input
            type="text"
            className="form-control form-control-lg w-full !rounded-md"
            id="signin-username"
            placeholder="username"
            value={username}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            onChange={(event) => setUsername(event.target.value)}
          />
        </div>

        <div className="col-span-12 mb-2">
          <label htmlFor="signin-password" className="form-label text-default block">
            Password
          </label>
          <div className="input-group">
            <input
              type={showPassword ? "text" : "password"}
              className="form-control form-control-lg !rounded-s-md"
              id="signin-password"
              placeholder="password"
              value={password}
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
            />
            <button
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="ti-btn ti-btn-light !rounded-s-none !mb-0"
              type="button"
            >
              <i className={`${showPassword ? "ri-eye-line" : "ri-eye-off-line"} align-middle`} />
            </button>
          </div>
        </div>

        <div className="col-span-12 grid mt-2">
          <button type="submit" className="ti-btn ti-btn-primary !bg-primary !text-white !font-medium" disabled={busy}>
            {busy ? <Working label="Signing in" /> : "Sign in"}
          </button>
        </div>
      </form>

      <div className="text-center my-4 authentication-barrier">
        <span>Device name</span>
      </div>

      <div className="text-center">
        {device === undefined ? null : device ? (
          <p className="text-[0.813rem] mb-0">
            <span className="badge bg-primary/10 text-primary me-1">{device.code}</span>
            <span className="font-semibold">{device.name}</span>
          </p>
        ) : (
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mb-0">
            This device is not registered.{" "}
            <a
              href="#!"
              className="text-primary"
              onClick={(event) => {
                event.preventDefault();
                setSettingUp(true);
              }}
            >
              Complete device setup
            </a>
          </p>
        )}
      </div>
    </AuthenticationLayout>
  );
}

/**
 * Typing in the claim code a master read out. Done once in the life of the device.
 */
function SetUpDevice({
  onDone,
  onCancel,
}: {
  readonly onDone: (device: ThisDevice) => void;
  readonly onCancel: () => void;
}): React.ReactElement {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      onDone((await api.claimDevice(code)).device);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Something went wrong");
      setBusy(false);
    }
  }

  return (
    <AuthenticationLayout
      title="Complete device setup"
      subtitle="Ask a system administrator to register this device, then enter the setup code they give you. This is done only once."
    >
      <form className="grid grid-cols-12 gap-y-4" onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}
        <div className="col-span-12">
          <label htmlFor="claim-code" className="form-label text-default">
            Setup code
          </label>
          <input
            type="text"
            className="form-control form-control-lg w-full !rounded-md text-center !tracking-[0.3em] font-semibold"
            id="claim-code"
            placeholder="XXXXXXXX"
            value={code}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
          />
        </div>
        <div className="col-span-12 grid mt-2">
          <button
            type="submit"
            className="ti-btn ti-btn-primary !bg-primary !text-white !font-medium"
            disabled={busy || code.trim() === ""}
          >
            {busy ? <Working label="Checking" /> : "Complete device setup"}
          </button>
        </div>
      </form>
      <div className="text-center">
        <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-4 mb-0">
          <a
            href="#!"
            className="text-primary"
            onClick={(event) => {
              event.preventDefault();
              onCancel();
            }}
          >
            Back to sign in
          </a>
        </p>
      </div>
    </AuthenticationLayout>
  );
}

/** The template's "authentication basic" page frame. */
export function AuthenticationLayout({
  title,
  subtitle,
  children,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
}): React.ReactElement {
  return (
    <div className="container">
      <div className="flex justify-center authentication authentication-basic items-center h-full text-defaultsize text-defaulttextcolor">
        <div className="grid grid-cols-12">
          <div className="xxl:col-span-4 xl:col-span-4 lg:col-span-4 md:col-span-3 sm:col-span-2" />
          <div className="xxl:col-span-4 xl:col-span-4 lg:col-span-4 md:col-span-6 sm:col-span-8 col-span-12">
            <div className="my-[2.5rem] flex justify-center">
              <img src={brandLogo("emblem")} alt="Oh-Ho Collectibles" className="signin-emblem" />
            </div>
            <div className="box">
              <div className="box-body !p-[3rem]">
                <p className="h5 font-semibold mb-2 text-center">{title}</p>
                {subtitle ? (
                  <p className="mb-4 text-[#8c9097] dark:text-white/50 opacity-[0.7] font-normal text-center">{subtitle}</p>
                ) : null}
                {children}
              </div>
            </div>
          </div>
          <div className="xxl:col-span-4 xl:col-span-4 lg:col-span-4 md:col-span-3 sm:col-span-2" />
        </div>
      </div>
    </div>
  );
}

/**
 * The second step of a sign in: the six digits from the authenticator app.
 *
 * A backup code is taken in the same box, because somebody whose phone is flat wants one
 * place to type what they have, not a second screen to go and find.
 */
function SecondStep({
  username,
  challenge,
  onSignedIn,
  onCancel,
}: {
  readonly username: string;
  readonly challenge: string;
  readonly onSignedIn: (session: SessionInfo) => void;
  readonly onCancel: () => void;
}): React.ReactElement {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(undefined);
    setBusy(true);
    try {
      onSignedIn(await api.signInWithCode(challenge, code.trim()));
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : "Something went wrong");
      setCode("");
      setBusy(false);
    }
  }

  return (
    <AuthenticationLayout title="Verification required" subtitle={`${username}, enter the code from your authenticator app.`}>
      <form className="grid grid-cols-12 gap-y-4" onSubmit={(event) => void submit(event)}>
        {error ? (
          <div className="col-span-12">
            <Alert tone="danger">{error}</Alert>
          </div>
        ) : null}
        <div className="col-span-12">
          <label htmlFor="signin-code" className="form-label text-default">
            Verification code
          </label>
          <input
            id="signin-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            className="form-control form-control-lg w-full !rounded-md text-center !tracking-[0.3em] font-semibold"
            placeholder="000000"
            value={code}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            onChange={(event) => setCode(event.target.value.toUpperCase())}
          />
          <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-2 mb-0">
            Enter the six-digit code from your authenticator app, or one of your backup codes.
          </p>
        </div>
        <div className="col-span-12 grid mt-2">
          <button
            type="submit"
            className="ti-btn ti-btn-primary !bg-primary !text-white !font-medium"
            disabled={busy || code.trim().length < 6}
          >
            {busy ? <Working label="Checking" /> : "Sign in"}
          </button>
        </div>
      </form>
      <div className="text-center">
        <p className="text-[0.75rem] text-[#8c9097] dark:text-white/50 mt-4 mb-0">
          <a
            href="#!"
            className="text-primary"
            onClick={(event) => {
              event.preventDefault();
              onCancel();
            }}
          >
            Start again
          </a>
        </p>
      </div>
    </AuthenticationLayout>
  );
}
